import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { createJobAndMatch } from "@/lib/jobs.functions";
import {
  Loader2, Sparkles, MapPin, Clock, ShieldCheck, BadgeCheck, Zap, Check, AlertCircle, Camera,
} from "lucide-react";
import { checkPostcode, prettyPostcode, normalizePostcode } from "@/lib/postcode";
import { lookupPostcode, autocompletePostcode, type PostcodeLookup } from "@/lib/postcode-lookup";
import { JobPhotosUploader, type JobPhoto } from "@/components/site/JobPhotosUploader";
import { AiJobBuilder } from "@/components/site/AiJobBuilder";

type Category = { id: string; slug: string; name: string };
import { PriceEstimateCard } from "@/components/site/PriceEstimateCard";


export const Route = createFileRoute("/post-job")({
  validateSearch: (s) =>
    z.object({
      postcode: z.string().optional(),
      category: z.string().optional(),
      helper_id: z.string().uuid().optional(),
      hold_id: z.string().uuid().optional(),
      starts_at: z.string().datetime().optional(),
    }).partial().parse(s),
  head: () => ({
    meta: [
      { title: "Post a job — Peopels" },
      { name: "description", content: "Tell us what you need. We'll instantly match you with the best vetted local Helper." },
      { property: "og:title", content: "Post a job — Peopels" },
      { property: "og:description", content: "Get matched with vetted local Helpers in minutes." },
    ],
    links: [{ rel: "canonical", href: "/post-job" }],
  }),
  component: PostJob,
});

const jobSchema = z.object({
  category_id: z.string().min(1, "Choose a service"),
  title: z.string().trim().min(6, "Add a short title (6+ characters)").max(120),
  description: z.string().trim().min(20, "Describe the task in a bit more detail (20+ characters)").max(2000),
  postcode: z.string().trim().refine((v) => checkPostcode(v).ok, "Enter a Greater London postcode (e.g. SW1A 1AA, E1 6AN)"),
});

type FormState = {
  category_id: string;
  title: string;
  description: string;
  postcode: string;
  scheduled_at: string;
  flexible_timing: boolean;
  urgency: "low" | "normal" | "high";
  budget_min: string;
  budget_max: string;
};

const DRAFT_KEY = "peoples:post-job-draft";

// --- Date/time quick picks -------------------------------------------------
type QuickPick = { id: string; label: string; sub: string; build: () => Date };
function makeQuickPicks(): QuickPick[] {
  const today = new Date();
  const at = (d: Date, h: number) => {
    const x = new Date(d);
    x.setHours(h, 0, 0, 0);
    return x;
  };
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const dow = today.getDay(); // 0=Sun
  const daysToSat = (6 - dow + 7) % 7 || 7;
  const saturday = new Date(today);
  saturday.setDate(today.getDate() + daysToSat);
  return [
    { id: "today-pm", label: "Today", sub: "Afternoon · 2 pm", build: () => at(today, 14) },
    { id: "today-eve", label: "Today", sub: "Evening · 6 pm", build: () => at(today, 18) },
    { id: "tom-am", label: "Tomorrow", sub: "Morning · 9 am", build: () => at(tomorrow, 9) },
    { id: "tom-pm", label: "Tomorrow", sub: "Afternoon · 2 pm", build: () => at(tomorrow, 14) },
    { id: "sat-am", label: "This Saturday", sub: "Morning · 10 am", build: () => at(saturday, 10) },
    { id: "flex", label: "I'm flexible", sub: "Pick the best time", build: () => new Date(0) },
  ];
}
function toLocalInput(d: Date) {
  // datetime-local needs YYYY-MM-DDTHH:mm in local time
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function PostJob() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const createJob = useServerFn(createJobAndMatch);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [photos, setPhotos] = useState<JobPhoto[]>([]);
  const [pcLookup, setPcLookup] = useState<PostcodeLookup | null>(null);
  const [pcChecking, setPcChecking] = useState(false);
  const [pcSuggestions, setPcSuggestions] = useState<string[]>([]);
  const [pcSuggestOpen, setPcSuggestOpen] = useState(false);
  const [form, setForm] = useState<FormState>({
    category_id: "",
    title: "",
    description: "",
    postcode: search.postcode ?? "",
    scheduled_at: "",
    flexible_timing: false,
    urgency: "normal",
    budget_min: "",
    budget_max: "",
  });

  const quickPicks = useMemo(makeQuickPicks, []);
  const [pickedQuick, setPickedQuick] = useState<string | null>(null);

  // Hydrate draft
  useEffect(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(DRAFT_KEY) : null;
      if (raw) {
        const draft = JSON.parse(raw) as Partial<FormState>;
        setForm((f) => ({ ...f, ...draft, postcode: f.postcode || draft.postcode || "" }));
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setAuthed(!!data.user);
      setUserId(data.user?.id ?? null);
    });
    supabase
      .from("categories")
      .select("id, slug, name")
      .order("name")
      .then(({ data }) => {
        const cats = (data ?? []) as Category[];
        setCategories(cats);
        if (search.category) {
          const match = cats.find((c) => c.slug === search.category);
          if (match) setForm((f) => (f.category_id ? f : { ...f, category_id: match.id }));
        }
      });
  }, [search.category]);

  useEffect(() => {
    void import("@/lib/funnel-track").then(({ track }) =>
      track("post_job_view", { category: search.category ?? null, has_postcode: !!search.postcode }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step completion tracking — fire once per step as it becomes complete.
  const stepFiredRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const fire = (e: "post_job_step_service" | "post_job_step_details" | "post_job_step_location") => {
      if (stepFiredRef.current.has(e)) return;
      stepFiredRef.current.add(e);
      void import("@/lib/funnel-track").then(({ track }) => track(e));
    };
    if (form.category_id) fire("post_job_step_service");
    if (form.title.trim().length >= 6 && form.description.trim().length >= 20) fire("post_job_step_details");
    if (checkPostcode(form.postcode).ok) fire("post_job_step_location");
  }, [form.category_id, form.title, form.description, form.postcode]);

  // Abandonment tracking — best-effort beacon when user leaves with partial progress.
  const submittedRef = useRef(false);
  useEffect(() => {
    const onLeave = () => {
      if (submittedRef.current) return;
      const filled = !!form.category_id || form.title.trim().length > 0 || form.description.trim().length > 0;
      if (!filled) return;
      void import("@/lib/funnel-track").then(({ track }) =>
        track("post_job_abandon", {
          has_category: !!form.category_id,
          has_title: form.title.trim().length > 0,
          has_description: form.description.trim().length >= 20,
          has_postcode: checkPostcode(form.postcode).ok,
        }),
      );
    };
    window.addEventListener("pagehide", onLeave);
    return () => window.removeEventListener("pagehide", onLeave);
  }, [form.category_id, form.title, form.description, form.postcode]);


  // Postcode lookup (debounced)
  const lookupTimer = useRef<number | null>(null);
  useEffect(() => {
    if (lookupTimer.current) window.clearTimeout(lookupTimer.current);
    setPcLookup(null);
    const check = checkPostcode(form.postcode);
    if (!check.ok) {
      // Autocomplete only when user is typing a plausible prefix
      const q = normalizePostcode(form.postcode);
      if (q.length >= 2 && q.length <= 6) {
        const ctrl = new AbortController();
        lookupTimer.current = window.setTimeout(async () => {
          const sugs = await autocompletePostcode(form.postcode, ctrl.signal);
          setPcSuggestions(sugs.slice(0, 5));
        }, 200);
        return () => ctrl.abort();
      }
      setPcSuggestions([]);
      return;
    }
    setPcSuggestions([]);
    setPcChecking(true);
    const ctrl = new AbortController();
    lookupTimer.current = window.setTimeout(async () => {
      const res = await lookupPostcode(form.postcode, ctrl.signal);
      setPcLookup(res);
      setPcChecking(false);
    }, 250);
    return () => ctrl.abort();
  }, [form.postcode]);

  const errors = useMemo(() => {
    const result = jobSchema.safeParse({
      category_id: form.category_id,
      title: form.title,
      description: form.description,
      postcode: form.postcode,
    });
    const out: Record<string, string> = {};
    if (!result.success) {
      for (const i of result.error.issues) {
        const k = i.path[0] as string;
        if (k && !out[k]) out[k] = i.message;
      }
    }
    if (form.budget_min && form.budget_max && Number(form.budget_min) > Number(form.budget_max)) {
      out.budget_max = "Max budget must be greater than min";
    }
    return out;
  }, [form]);

  const completedSteps = useMemo(() => {
    let n = 0;
    if (form.category_id) n++;
    if (form.title.trim().length >= 6 && form.description.trim().length >= 20) n++;
    if (checkPostcode(form.postcode).ok) n++;
    return n;
  }, [form]);

  const canSubmit = Object.keys(errors).length === 0 && photos.length > 0;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched({ category_id: true, title: true, description: true, postcode: true, budget_max: true });
    if (!canSubmit) {
      if (Object.keys(errors).length === 0 && photos.length === 0) {
        setSubmitError("Please add at least one photo so Helpers can quote accurately and we have a record before the job starts.");
        document.getElementById("photos-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setSubmitError("Please fix the highlighted fields and try again.");
      // Scroll to first error for mobile
      const firstKey = Object.keys(errors)[0];
      if (firstKey) document.getElementById(firstKey)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitError(null);
    if (!authed) {
      navigate({ to: "/auth", search: { redirect: "/post-job" } });
      return;
    }
    setSubmitting(true);
    try {
      const res = await createJob({
        data: {
          category_id: form.category_id,
          title: form.title.trim(),
          description: form.description.trim(),
          postcode: prettyPostcode(form.postcode),
          scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
          flexible_timing: form.flexible_timing,
          urgency: form.urgency,
          budget_min: form.budget_min ? Number(form.budget_min) : null,
          budget_max: form.budget_max ? Number(form.budget_max) : null,
          photos: photos.filter((p) => p.path).map((p) => p.path),
        },
      });
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      void import("@/lib/funnel-track").then(({ track }) =>
        track("post_job_submit", {
          category_id: form.category_id,
          matched: res.matched_count,
          urgency: form.urgency,
          photos: photos.length,
        }),
      );
      submittedRef.current = true;
      toast.success(
        res.matched_count > 0
          ? `Job posted — ${res.matched_count} Helper${res.matched_count === 1 ? "" : "s"} matched`
          : "Job posted — we'll notify you as soon as a Helper accepts",
      );
      navigate({ to: "/dashboard", search: { posted: 1 } as never });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not post job. Please try again.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const showErr = (k: string) => touched[k] && errors[k];
  const postcodeCheck = checkPostcode(form.postcode);

  function pickQuick(p: QuickPick) {
    setPickedQuick(p.id);
    if (p.id === "flex") {
      setForm((f) => ({ ...f, flexible_timing: true, scheduled_at: "" }));
    } else {
      setForm((f) => ({ ...f, flexible_timing: false, scheduled_at: toLocalInput(p.build()) }));
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-2xl flex-1 px-4 pb-32 pt-8 sm:px-6 sm:pb-12 sm:pt-12 lg:px-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-medium text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Free to post, no account needed yet
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Tell us what you need</h1>
          <p className="mt-2 text-muted-foreground">Three quick steps. You'll only sign in when your job is ready to send.</p>

          {/* Progress */}
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
              <span className={completedSteps >= 1 ? "text-primary" : ""}>1. Service</span>
              <span className={completedSteps >= 2 ? "text-primary" : ""}>2. Details</span>
              <span className={completedSteps >= 3 ? "text-primary" : ""}>3. Where & when</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-primary-glow transition-all"
                style={{ width: `${(completedSteps / 3) * 100}%` }}
                aria-hidden
              />
            </div>
          </div>
        </div>

        <AiJobBuilder
          categories={categories}
          postcode={form.postcode}
          onApply={(a, meta) => {
            setForm((f) => ({
              ...f,
              category_id: a.category_id ?? f.category_id,
              title: a.title,
              description: a.description,
              urgency: a.urgency,
            }));
            setTouched((t) => ({ ...t, category_id: true, title: true, description: true }));
            void import("@/lib/funnel-track").then(({ track }) =>
              track("post_job_ai_applied", {
                category_slug: meta.category_slug,
                confidence: meta.confidence,
                est_minutes: meta.estimated_duration_minutes,
              }),
            );
            setTimeout(() => {
              document.getElementById("title")?.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 50);
          }}
        />

        <form onSubmit={onSubmit} noValidate className="space-y-6 rounded-2xl border border-border bg-card p-5 shadow-card sm:p-8">
          {submitError && (
            <div role="alert" className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{submitError}</span>
            </div>
          )}

          {/* Service */}
          <div className="space-y-2">
            <Label htmlFor="category_id">Service</Label>
            <select
              id="category_id"
              required
              value={form.category_id}
              onChange={(e) => setForm({ ...form, category_id: e.target.value })}
              onBlur={() => setTouched((t) => ({ ...t, category_id: true }))}
              aria-invalid={!!showErr("category_id")}
              aria-describedby={showErr("category_id") ? "category-err" : undefined}
              className={`h-11 w-full rounded-md border bg-background px-3 text-base ${showErr("category_id") ? "border-destructive" : "border-input"}`}
            >
              <option value="">Select a service…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {showErr("category_id") && <p id="category-err" role="alert" className="text-xs text-destructive">{errors.category_id}</p>}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Job title</Label>
            <Input
              id="title" required maxLength={120}
              placeholder="e.g. Mount 55″ TV on plasterboard wall"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              onBlur={() => setTouched((t) => ({ ...t, title: true }))}
              aria-invalid={!!showErr("title")}
              aria-describedby={showErr("title") ? "title-err" : undefined}
              className={`h-11 text-base ${showErr("title") ? "border-destructive" : ""}`}
            />
            {showErr("title") && <p id="title-err" role="alert" className="text-xs text-destructive">{errors.title}</p>}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">What needs doing?</Label>
            <Textarea
              id="description" required rows={5} maxLength={2000}
              placeholder="Include access, materials, anything tricky…"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              onBlur={() => setTouched((t) => ({ ...t, description: true }))}
              aria-invalid={!!showErr("description")}
              aria-describedby={showErr("description") ? "description-err" : "description-hint"}
              className={`text-base ${showErr("description") ? "border-destructive" : ""}`}
            />
            <div className="flex items-center justify-between">
              {showErr("description") ? (
                <p id="description-err" role="alert" className="text-xs text-destructive">{errors.description}</p>
              ) : (
                <p id="description-hint" className="text-xs text-muted-foreground">More detail = better matches & accurate quotes.</p>
              )}
              <span className="text-xs text-muted-foreground">{form.description.length}/2000</span>
            </div>
          </div>

          {/* Photos */}
          <div id="photos-section" className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Add photos <span className="text-xs font-normal text-rose-600">(required — at least 1)</span></Label>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Camera className="h-3.5 w-3.5" /> Helps Helpers quote accurately
              </span>
            </div>
            <JobPhotosUploader userId={userId} value={photos} onChange={setPhotos} />
            {photos.length === 0 && (
              <p className="text-xs text-muted-foreground">A "before" photo protects both sides if anything's disputed later.</p>
            )}
          </div>

          {/* Postcode + Date */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="relative space-y-2">
              <Label htmlFor="postcode"><MapPin className="mr-1 inline h-3.5 w-3.5" />London postcode</Label>
              <div className="relative">
                <Input
                  id="postcode" required placeholder="e.g. SW1A 1AA" maxLength={8}
                  autoComplete="postal-code"
                  inputMode="text"
                  value={form.postcode}
                  onChange={(e) => { setForm({ ...form, postcode: e.target.value.toUpperCase() }); setPcSuggestOpen(true); }}
                  onFocus={() => setPcSuggestOpen(true)}
                  onBlur={() => { setTouched((t) => ({ ...t, postcode: true })); setTimeout(() => setPcSuggestOpen(false), 150); }}
                  aria-invalid={!!showErr("postcode")}
                  aria-describedby={showErr("postcode") ? "postcode-err" : pcLookup ? "postcode-ok" : undefined}
                  className={`h-11 pr-9 text-base uppercase ${showErr("postcode") ? "border-destructive" : postcodeCheck.ok ? "border-emerald-500/60" : ""}`}
                />
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {pcChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : postcodeCheck.ok ? <Check className="h-4 w-4 text-emerald-600" /> : null}
                </span>
                {pcSuggestOpen && pcSuggestions.length > 0 && (
                  <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg">
                    {pcSuggestions.map((s) => (
                      <li key={s}>
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-accent"
                          onMouseDown={(e) => { e.preventDefault(); setForm((f) => ({ ...f, postcode: s })); setPcSuggestOpen(false); }}
                        >
                          {s}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {showErr("postcode") ? (
                <p id="postcode-err" role="alert" className="text-xs text-destructive">{!postcodeCheck.ok ? postcodeCheck.message : errors.postcode}</p>
              ) : pcLookup ? (
                <p id="postcode-ok" className="text-xs text-emerald-700 dark:text-emerald-400">
                  <Check className="mr-1 inline h-3 w-3" /> We cover {pcLookup.area}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">We'll only show Helpers who serve your area.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="scheduled_at"><Clock className="mr-1 inline h-3.5 w-3.5" />Preferred time</Label>
              <Input
                id="scheduled_at" type="datetime-local"
                value={form.scheduled_at}
                min={toLocalInput(new Date())}
                onChange={(e) => { setForm({ ...form, scheduled_at: e.target.value, flexible_timing: false }); setPickedQuick(null); }}
                className="h-11 text-base"
              />
            </div>
          </div>

          {/* Quick date picks */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Or pick a time fast</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {quickPicks.map((p) => {
                const active = pickedQuick === p.id || (p.id === "flex" && form.flexible_timing);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickQuick(p)}
                    className={`rounded-xl border px-3 py-2.5 text-left transition ${
                      active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card hover:bg-accent/40"
                    }`}
                  >
                    <p className="text-sm font-medium text-foreground">{p.label}</p>
                    <p className="text-xs text-muted-foreground">{p.sub}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Urgency & budget — collapsed to reduce friction; optional */}
          <details className="group rounded-xl border border-border bg-card/40 p-4 open:bg-card">
            <summary className="cursor-pointer list-none text-sm font-medium text-foreground">
              <span className="inline-flex w-full items-center justify-between gap-3">
                Add urgency or budget <span className="text-xs font-normal text-muted-foreground">Optional</span>
              </span>
            </summary>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="urgency">Urgency</Label>
                <select
                  id="urgency"
                  value={form.urgency}
                  onChange={(e) => setForm({ ...form, urgency: e.target.value as "low" | "normal" | "high" })}
                  className="h-11 w-full rounded-md border border-input bg-background px-3 text-base"
                >
                  <option value="low">Flexible</option>
                  <option value="normal">Normal</option>
                  <option value="high">Urgent</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="budget_min">Budget min (£)</Label>
                <Input id="budget_min" type="number" min={0} step="1" inputMode="numeric" placeholder="40"
                  value={form.budget_min}
                  onChange={(e) => setForm({ ...form, budget_min: e.target.value })}
                  className="h-11 text-base" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="budget_max">Budget max (£)</Label>
                <Input id="budget_max" type="number" min={0} step="1" inputMode="numeric" placeholder="120"
                  value={form.budget_max}
                  onChange={(e) => setForm({ ...form, budget_max: e.target.value })}
                  onBlur={() => setTouched((t) => ({ ...t, budget_max: true }))}
                  aria-invalid={!!showErr("budget_max")}
                  aria-describedby={showErr("budget_max") ? "budget-err" : undefined}
                  className={`h-11 text-base ${showErr("budget_max") ? "border-destructive" : ""}`}
                />
                {showErr("budget_max") && <p id="budget-err" role="alert" className="text-xs text-destructive">{errors.budget_max}</p>}
              </div>
            </div>
          </details>


          {/* Live price estimate (visible as soon as category + postcode are valid) */}
          {(() => {
            const slug = categories.find((c) => c.id === form.category_id)?.slug ?? "";
            if (!slug || !postcodeCheck.ok) return null;
            return (
              <PriceEstimateCard
                categorySlug={slug}
                postcode={form.postcode}
                urgency={form.urgency}
              />
            );
          })()}

          {/* Desktop submit (mobile uses sticky bar below) */}
          <Button type="submit" size="lg" className="hidden w-full sm:inline-flex" disabled={submitting}>
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Matching Helpers…</> : "Post job & see quotes"}
          </Button>

          {/* Trust micro-copy */}
          <div className="grid gap-3 pt-2 sm:grid-cols-3">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><BadgeCheck className="h-3.5 w-3.5 text-primary" /> ID-verified Helpers</span>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Funds released when done</span>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Zap className="h-3.5 w-3.5 text-primary" /> Free to post, cancel any time</span>
          </div>

          {authed === false && (
            <p className="text-center text-xs text-muted-foreground">You'll be asked to sign in before your job is posted — your draft is saved.</p>
          )}
        </form>

        {/* Sticky mobile submit */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 py-3 backdrop-blur sm:hidden">
          <Button
            type="button"
            size="lg"
            className="w-full"
            disabled={submitting}
            onClick={() => {
              const formEl = document.querySelector("form");
              if (formEl) formEl.requestSubmit();
            }}
          >
            {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Matching Helpers…</> : "Post job & see quotes"}
          </Button>
          <p className="mt-1.5 text-center text-[11px] text-muted-foreground">Free to post. Your card is authorized at booking and only released when the job is done.</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
